import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiEdit2, FiLock, FiPlus, FiShield, FiTrash2, FiUsers, FiX } from "react-icons/fi";
import { PermissionCatalog, usePermissions } from "../Auth/PermissionsContext";
import { expandGrants, toggleToken } from "../Auth/permissionTokens";
import GatedButton from "../Components/GatedButton";
import { RbacLeak, Role, rolesClient } from "./Permissions/rolesClient";
import { groupRolesByFamily } from "../Helper/roleFamilies";
import { SCOPE_TAG_CLASSES, describeRoleContentScope } from "../Helper/roleContentScope";

/** RBAC roles : bundles of feature + content-type read/write permissions, grouped so several can be
 * granted in one click. Built-in (system) roles can be tuned but not deleted. */
export default function Permissions(): React.ReactElement {
	const { can, catalog } = usePermissions();
	const canWrite = can("feature:roles:write");

	const [roles, setRoles] = useState<Role[]>([]);
	const [leaks, setLeaks] = useState<RbacLeak[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState<Role | "new" | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [list, currentLeaks] = await Promise.all([rolesClient.list(), rolesClient.leaks()]);
			setRoles(list);
			setLeaks(currentLeaks);
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Impossible de charger les rôles.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const onDelete = async (role: Role) => {
		if (!window.confirm(`Supprimer le rôle « ${role.name} » ?`)) return;
		try {
			await rolesClient.remove(role.key);
			toast.success("Rôle supprimé.");
			void load();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Suppression impossible.");
		}
	};

	return (
		<div className="mx-auto w-full max-w-5xl px-6 py-8">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-slate-900">Rôles & permissions</h1>
					<p className="mt-1 text-sm text-slate-500">
						Définissez des rôles regroupant des permissions par fonctionnalité et par type de contenu.
					</p>
				</div>
				{catalog && (
					<GatedButton
						token="feature:roles:write"
						onClick={() => setEditing("new")}
						className="inline-flex items-center gap-2 rounded-xl bg-[#0095DB] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8]"
					>
						<FiPlus className="h-4 w-4" /> Nouveau rôle
					</GatedButton>
				)}
			</div>

			<LeakBanner leaks={leaks} />

			{loading ? (
				<p className="mt-6 text-slate-400">Chargement…</p>
			) : (
				groupRolesByFamily(roles).map((family) => (
					<section key={family.key} className="mt-6">
						<h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{family.label}</h2>
						<div className="grid gap-3 sm:grid-cols-2">
							{family.roles.map((role) => (
						<div key={role.key} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
							<div className="flex items-start justify-between gap-3">
								<div className="flex items-start gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
										<FiShield className="h-5 w-5" />
									</div>
									<div>
										<div className="flex items-center gap-2">
											<p className="font-semibold text-slate-900">{role.name}</p>
											{role.isSystem && (
												<span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
													<FiLock className="h-3 w-3" /> Système
												</span>
											)}
										</div>
										<p className="mt-0.5 text-xs text-slate-400">{role.key}</p>
										{role.description && <p className="mt-1 text-sm text-slate-500">{role.description}</p>}
									</div>
								</div>
							</div>
							{/* Perimetre de contenu, en clair : « Tout le contenu » pour un role qui a tout,
							    le nom du groupe quand il est couvert en entier, sinon le titre de chaque
							    type. Evite d'ouvrir l'editeur pour savoir sur quoi porte un role. */}
							<div className="mt-3 flex flex-wrap gap-1.5" aria-label="Périmètre de contenu">
								{describeRoleContentScope(role.permissions, catalog).map((tag) => (
									<span
										key={`${tag.tone}:${tag.label}`}
										title={tag.hint}
										className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${SCOPE_TAG_CLASSES[tag.tone]}`}
									>
										{tag.label}
									</span>
								))}
							</div>
							<div className="mt-4 flex items-center justify-between">
								<span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
									<FiUsers className="h-3.5 w-3.5" /> {role.userCount} utilisateur{role.userCount === 1 ? "" : "s"}
								</span>
								<div className="flex gap-1">
									<GatedButton
										token="feature:roles:write"
										onClick={() => setEditing(role)}
										className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
										title="Modifier"
									>
										<FiEdit2 className="h-4 w-4" />
									</GatedButton>
									{!role.isSystem && (
										<GatedButton
											token="feature:roles:write"
											onClick={() => onDelete(role)}
											className="rounded-lg p-2 text-rose-500 transition hover:bg-rose-50"
											title="Supprimer"
										>
											<FiTrash2 className="h-4 w-4" />
										</GatedButton>
									)}
								</div>
							</div>
						</div>
							))}
						</div>
					</section>
				))
			)}

			{editing && canWrite && catalog && (
				<RoleEditor
					catalog={catalog}
					role={editing === "new" ? null : editing}
					onClose={() => setEditing(null)}
					onSaved={(savedLeaks) => {
						setEditing(null);
						// La réponse du POST/PUT contient déjà l'analyse complète : la bannière se
						// rafraîchit immédiatement, sans attendre le rechargement de la liste.
						if (savedLeaks) setLeaks(savedLeaks);
						void load();
					}}
				/>
			)}
		</div>
	);
}

/** Bannière de cohérence RBAC : rouge si au moins une fuite « error », ambre si uniquement des
 * avertissements, verte quand l'analyse ne détecte rien. Chaque fuite est listée avec son
 * explication serveur exacte + les jetons en excès (déjà plafonnés côté serveur). */
function LeakBanner({ leaks }: { leaks: RbacLeak[] | null }): React.ReactElement | null {
	if (leaks === null) return null; // analyse pas encore chargée

	if (leaks.length === 0) {
		return (
			<div className="mt-6 flex items-center gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
				<FiCheckCircle className="h-4 w-4 shrink-0" />
				<span>Aucune fuite détectée : le système de rôles est cohérent.</span>
			</div>
		);
	}

	const hasError = leaks.some((l) => l.severity === "error");
	const frame = hasError ? "border-rose-200 bg-rose-50 text-rose-900" : "border-amber-200 bg-amber-50 text-amber-900";
	const badge = (severity: string) =>
		severity === "error"
			? "bg-rose-100 text-rose-700"
			: "bg-amber-100 text-amber-800";

	return (
		<div role="alert" className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${frame}`}>
			<p className="flex items-center gap-2 font-semibold">
				{hasError ? <FiAlertOctagon className="h-4 w-4 shrink-0" /> : <FiAlertTriangle className="h-4 w-4 shrink-0" />}
				{hasError
					? `Fuites d'escalade de privilèges détectées (${leaks.length})`
					: `Avertissements de cohérence des rôles (${leaks.length})`}
			</p>
			<ul className="mt-2 space-y-2">
				{leaks.map((l, i) => (
					<li key={`${l.roleKey}-${l.via}-${l.reachableRoleKey ?? ""}-${i}`} className="flex flex-col gap-1">
						<span>
							<span className={`mr-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${badge(l.severity)}`}>
								{l.severity === "error" ? "Erreur" : "Avertissement"}
							</span>
							{l.explanation}
						</span>
						{l.excessTokens.length > 0 && (
							<span className="break-words font-mono text-[11px] opacity-70">{l.excessTokens.join(" · ")}</span>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}

function RoleEditor({
	catalog,
	role,
	onClose,
	onSaved
}: {
	catalog: PermissionCatalog;
	role: Role | null;
	onClose: () => void;
	/** Reçoit les fuites renvoyées par le POST/PUT (analyse complète) pour rafraîchir la bannière. */
	onSaved: (leaks: RbacLeak[] | null) => void;
}): React.ReactElement {
	const isEdit = !!role;
	const [key, setKey] = useState(role?.key ?? "");
	const [name, setName] = useState(role?.name ?? "");
	const [description, setDescription] = useState(role?.description ?? "");
	const [tokens, setTokens] = useState<Set<string>>(() => expandGrants(catalog, role?.permissions ?? []));
	const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
	const [saving, setSaving] = useState(false);

	const has = (token: string) => tokens.has(token);
	const setFeature = (k: string, action: "read" | "write" | "purge", on: boolean) =>
		setTokens((t) => toggleToken(t, "feature", k, action, on));
	const setContent = (k: string, action: "read" | "write" | "publish" | "promote", on: boolean) =>
		setTokens((t) => toggleToken(t, "content", k, action, on));

	// Section → features, preserving catalog order.
	const sections = useMemo(() => {
		const map = new Map<string, typeof catalog.features>();
		for (const f of catalog.features) {
			if (!map.has(f.section)) map.set(f.section, []);
			map.get(f.section)!.push(f);
		}
		return Array.from(map.entries());
	}, [catalog]);

	const setGroup = (typeKeys: string[], action: "read" | "write" | "publish" | "promote", on: boolean) =>
		setTokens((prev) => {
			let next = prev;
			for (const tk of typeKeys) next = toggleToken(next, "content", tk, action, on);
			return next;
		});
	const groupAll = (typeKeys: string[], action: "read" | "write" | "publish" | "promote") =>
		typeKeys.length > 0 && typeKeys.every((tk) => tokens.has(`content:${tk}:${action}`));

	const save = async () => {
		setSaving(true);
		try {
			const body = { name, description: description || null, permissions: Array.from(tokens) };
			let result;
			if (isEdit) {
				result = await rolesClient.update(role!.key, body);
				toast.success("Rôle mis à jour.");
			} else {
				result = await rolesClient.create(key.trim().toLowerCase(), body);
				toast.success("Rôle créé.");
			}
			onSaved(result?.leaks ?? null);
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Enregistrement impossible.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/40 p-4">
			<div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
				<div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
					<h2 className="text-lg font-semibold text-slate-900">{isEdit ? `Modifier « ${role!.name} »` : "Nouveau rôle"}</h2>
					<button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
						<FiX className="h-5 w-5" />
					</button>
				</div>

				<div className="space-y-6 overflow-auto px-6 py-5">
					<div className="grid gap-4 sm:grid-cols-2">
						{!isEdit && (
							<label className="block">
								<span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Clé (slug)</span>
								<input
									value={key}
									onChange={(e) => setKey(e.target.value)}
									placeholder="ex. responsable-contenu"
									className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
								/>
							</label>
						)}
						<label className="block">
							<span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nom</span>
							<input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
						</label>
						<label className="block sm:col-span-2">
							<span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span>
							<input
								value={description ?? ""}
								onChange={(e) => setDescription(e.target.value)}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
							/>
						</label>
					</div>

					{/* Feature permissions */}
					<section>
						<h3 className="mb-2 text-sm font-bold text-slate-800">Fonctionnalités</h3>
						<div className="space-y-4">
							{sections.map(([section, features]) => (
								<div key={section} className="rounded-xl border border-slate-200">
									<div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
										{section}
									</div>
									<div className="divide-y divide-slate-50">
										{features.map((f) => (
											<div key={f.key} className="flex items-center justify-between px-4 py-2.5 text-sm">
												<span className="text-slate-700">{f.label}</span>
												<div className="flex items-center gap-5">
													<Check label="Lecture" checked={has(`feature:${f.key}:read`)} onChange={(on) => setFeature(f.key, "read", on)} />
													<Check
														label="Écriture"
														disabled={!f.supportsWrite}
														checked={has(`feature:${f.key}:write`)}
														onChange={(on) => setFeature(f.key, "write", on)}
													/>
													{/* Rendue désactivée (pas masquée) quand la fonctionnalité ne supporte pas la
													    purge, pour garder les colonnes alignées : même règle que « Écriture ». */}
													<Check
														label="Purge"
														disabled={f.supportsPurge !== true}
														checked={has(`feature:${f.key}:purge`)}
														onChange={(on) => setFeature(f.key, "purge", on)}
													/>
												</div>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					</section>

					{/* Content-type permissions */}
					<section>
						<h3 className="mb-2 text-sm font-bold text-slate-800">Types de contenu</h3>
						<div className="space-y-3">
							{catalog.groups.map((g) => {
								const open = openGroups.has(g.key);
								return (
									<div key={g.key} className="rounded-xl border border-slate-200">
										<div className="flex items-center justify-between px-4 py-2.5">
											<button
												type="button"
												onClick={() =>
													setOpenGroups((prev) => {
														const next = new Set(prev);
														if (next.has(g.key)) next.delete(g.key);
														else next.add(g.key);
														return next;
													})
												}
												className="flex items-center gap-2 text-sm font-semibold text-slate-700"
											>
												{open ? <FiChevronDown className="h-4 w-4" /> : <FiChevronRight className="h-4 w-4" />}
												{g.label}
												<span className="text-xs font-normal text-slate-400">({g.typeKeys.length})</span>
											</button>
											{/* 4 colonnes : Lire / Écrire / Publier (preview) / Production (promote).
											    Les implications sont celles de toggleToken : promote coche publish + read ;
											    décocher publish retire promote ; écrire reste orthogonal aux deux. */}
											<div className="flex items-center gap-4">
												<Check label="Tout lire" checked={groupAll(g.typeKeys, "read")} onChange={(on) => setGroup(g.typeKeys, "read", on)} />
												<Check label="Tout écrire" checked={groupAll(g.typeKeys, "write")} onChange={(on) => setGroup(g.typeKeys, "write", on)} />
												<Check label="Tout publier" checked={groupAll(g.typeKeys, "publish")} onChange={(on) => setGroup(g.typeKeys, "publish", on)} />
												<Check label="Toute la prod." checked={groupAll(g.typeKeys, "promote")} onChange={(on) => setGroup(g.typeKeys, "promote", on)} />
											</div>
										</div>
										{open && (
											<div className="divide-y divide-slate-50 border-t border-slate-100">
												{g.typeKeys.map((tk) => {
													const type = catalog.contentTypes.find((t) => t.key === tk);
													return (
														<div key={tk} className="flex items-center justify-between px-4 py-2 pl-10 text-sm">
															<span className="text-slate-600">{type?.label ?? tk}</span>
															<div className="flex items-center gap-4">
																<Check label="Lecture" checked={has(`content:${tk}:read`)} onChange={(on) => setContent(tk, "read", on)} />
																<Check label="Écriture" checked={has(`content:${tk}:write`)} onChange={(on) => setContent(tk, "write", on)} />
																<Check label="Publier" checked={has(`content:${tk}:publish`)} onChange={(on) => setContent(tk, "publish", on)} />
																<Check label="Production" checked={has(`content:${tk}:promote`)} onChange={(on) => setContent(tk, "promote", on)} />
															</div>
														</div>
													);
												})}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</section>
				</div>

				<div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
					<button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
						Annuler
					</button>
					<button
						type="button"
						onClick={save}
						disabled={saving || !name.trim() || (!isEdit && !key.trim())}
						className="inline-flex items-center gap-2 rounded-xl bg-[#0095DB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8] disabled:opacity-50"
					>
						<FiPlus className="h-4 w-4" /> {isEdit ? "Enregistrer" : "Créer le rôle"}
					</button>
				</div>
			</div>
		</div>
	);
}

function Check({
	label,
	checked,
	onChange,
	disabled
}: {
	label: string;
	checked: boolean;
	onChange: (on: boolean) => void;
	disabled?: boolean;
}): React.ReactElement {
	return (
		<label className={`flex items-center gap-1.5 text-xs ${disabled ? "text-slate-300" : "text-slate-600"}`}>
			<input type="checkbox" disabled={disabled} checked={checked} onChange={(e) => onChange(e.target.checked)} />
			{label}
		</label>
	);
}
